import type { ZonedDateTime } from '@internationalized/date';
import type { ModalProps } from '@workramp/ui';
import {
  getLocalTimeZone,
  isSameDay,
  isToday,
  now,
  parseAbsolute,
  Time,
  toCalendarDateTime,
} from '@internationalized/date';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  DatePicker,
  TimePicker,
  InputGroup,
  Stack,
  Row,
  Col,
} from '@workramp/ui';
import { useState } from 'react';

export enum AnnouncementBannerScheduleModalModes {
  SCHEDULE = 'schedule',
  EDIT_SCHEDULE = 'edit_schedule',
  EDIT_ENDTIME = 'edit_endtime',
}

interface AnnouncementBannerScheduleModalProps extends Omit<ModalProps, 'children'> {
  isOpen: boolean;
  startTime: number;
  endTime: number;
  onSave: (data: { startTime?: number; endTime?: number }) => void;
  mode: AnnouncementBannerScheduleModalModes;
}

export const AnnouncementBannerScheduleModal = ({
  isOpen,
  startTime,
  endTime,
  onClose,
  onSave,
  mode = AnnouncementBannerScheduleModalModes.SCHEDULE,
}: AnnouncementBannerScheduleModalProps) => {
  const [newStartTime, setNewStartTime] = useState<number | null>(startTime);
  const [newEndTime, setNewEndTime] = useState<number | null>(endTime);
  const timezone = getLocalTimeZone();
  const currentTime = now(timezone).set({ second: 0, millisecond: 0 });
  const startDateTime = newStartTime ? parseAbsolute(new Date(newStartTime).toISOString(), timezone) : null;
  const endDateTime = newEndTime ? parseAbsolute(new Date(newEndTime).toISOString(), timezone) : null;

  const handleStartDateChange = (date: ZonedDateTime) => {
    const zeroedDate = date?.set({ second: 0, millisecond: 0 });

    // If the new start date is today, set the start time to the current time
    if (isToday(zeroedDate, timezone)) {
      const newDateTime = toCalendarDateTime(zeroedDate, new Time(currentTime.hour, currentTime.minute));
      setNewStartTime(newDateTime?.toDate(timezone).valueOf() || null);
      return;
    }

    // If the new start date is after the current end date, push the end date 30 minutes after the new start date
    if (endDateTime && zeroedDate.compare(endDateTime) > 0) {
      const newEndDateTime = zeroedDate.add({ minutes: 30 });
      setNewEndTime(newEndDateTime?.toDate().valueOf() || null);
    }

    setNewStartTime(zeroedDate?.toDate().valueOf() || null);
  };

  const handleEndDateChange = (date: ZonedDateTime) => {
    const zeroedDate = date?.set({ second: 0, millisecond: 0 });

    // If the new end date is the same as the start date, push the end time 30 minutes after the start time
    if (isSameDay(zeroedDate, startDateTime)) {
      const newDateTime = toCalendarDateTime(zeroedDate, startDateTime.add({ minutes: 30 }));
      setNewEndTime(newDateTime?.toDate(timezone).valueOf() || null);
      return;
    }

    setNewEndTime(zeroedDate?.toDate().valueOf() || null);
  };

  const handleStartTimeChange = (date: ZonedDateTime) => {
    const zeroedDate = date?.set({ second: 0, millisecond: 0 });

    // If the new start time is after the current end time, push the end time 30 minutes after the new start time
    if (endDateTime && zeroedDate.compare(endDateTime) >= 0) {
      const newEndDateTime = zeroedDate.add({ minutes: 30 });
      setNewEndTime(newEndDateTime?.toDate().valueOf() || null);
    }

    setNewStartTime(zeroedDate?.toDate().valueOf() || null);
  };

  const handleEndTimeChange = (date: ZonedDateTime) => {
    const zeroedDate = date?.set({ second: 0, millisecond: 0 });
    setNewEndTime(zeroedDate?.toDate().valueOf() || null);
  };

  const handleSubmit = () => {
    onSave({ startTime: newStartTime, endTime: newEndTime });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader
        title={
          mode === AnnouncementBannerScheduleModalModes.SCHEDULE
            ? 'Schedule Banner Visibility'
            : 'Edit Visibility Schedule'
        }
      />
      <ModalBody>
        <Stack size={4}>
          {mode !== AnnouncementBannerScheduleModalModes.EDIT_ENDTIME && (
            <InputGroup label="Start">
              <DatePicker
                hideLabel
                forceShowYear
                label="Start Date"
                placeholder="Start Date"
                value={startDateTime}
                onChange={handleStartDateChange}
                minValue={currentTime}
              />
              <TimePicker
                hideLabel
                label="Start Time"
                placeholder="Start Time"
                value={startDateTime}
                onChange={handleStartTimeChange}
                minValue={currentTime}
              />
            </InputGroup>
          )}
          <InputGroup label="End">
            <DatePicker
              hideLabel
              forceShowYear
              label="End Date"
              placeholder="End"
              value={endTime ? endDateTime : undefined}
              onChange={handleEndDateChange}
              minValue={startDateTime}
            />
            <TimePicker
              hideLabel
              label="End Time"
              placeholder="End Time"
              value={endDateTime}
              onChange={handleEndTimeChange}
              minValue={startDateTime}
            />
          </InputGroup>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Row justify="space-between" align="center">
          {mode === AnnouncementBannerScheduleModalModes.EDIT_SCHEDULE && (
            <Col>
              <Button
                size="lg"
                onPress={() => {
                  onSave({ startTime: null, endTime: null });
                }}
                color="danger"
                variant="outline"
              >
                Remove Schedule
              </Button>
            </Col>
          )}
          {mode === AnnouncementBannerScheduleModalModes.EDIT_ENDTIME && (
            <Col>
              <Button size="lg" onPress={() => onSave({ endTime: null })} color="danger" variant="outline">
                Remove End Date
              </Button>
            </Col>
          )}
          <Col xs="auto">
            <Button size="lg" onPress={handleSubmit}>
              {mode === AnnouncementBannerScheduleModalModes.SCHEDULE ? 'Schedule' : 'Save'}
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
